import {
  ClerkProvider,
  OrganizationSwitcher,
  SignedIn,
  SignedOut,
  SignOutButton,
  useOrganization,
  useOrganizationList,
  useUser,
} from '@clerk/nextjs';
import { GoogleAnalytics } from '@next/third-parties/google';
import chunk from 'lodash/chunk';
import { Users as UsersIcon } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';
import {
  type CSSProperties,
  type ReactNode,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { clearUserForSentry, setUserForSentry } from 'sentry.client.config';
import { CmdKSwitcher } from '@/code/frontend/components/cmd-k';
import { DashboardNavigation } from '@/code/frontend/components/DashboardNavigation';
import { DialogProvider } from '@/code/frontend/components/hooks/use-dialog';
import { Notifications } from '@/code/frontend/components/Notifications';
import OrganizationNavigation from '@/code/frontend/components/organizations/OrganizationNavigation';
import { PendingInvitationsBanner } from '@/code/frontend/components/organizations/PendingInvitationsBanner';
import { TeamManagementPage } from '@/code/frontend/components/organizations/TeamManagementPage';
import { AuthQueryBoundary } from '@/code/frontend/components/providers/AuthQueryBoundary';
import { CalendarScopeAutoHeal } from '@/code/frontend/components/providers/CalendarScopeAutoHeal';
import { FullstoryProvider } from '@/code/frontend/components/providers/FullstoryProvider';
import PostHogProviderWrapper from '@/code/frontend/components/providers/PosthogProvider';
import { CustomizedUserButton } from '@/code/frontend/components/ui/Clerk/CustomizedUserButton';
import { Skeleton } from '@/code/frontend/components/ui/skeleton';
import { Toaster } from '@/code/frontend/components/ui/toaster';
import { LinkRouter } from '@/code/lib/routes';
import { NO_SESSION_REDIRECT_LATCH_KEY } from '@/code/lib/sentry/trpcErrorCodes';
import { cn } from '@/code/lib/utils/cn';
import { getAllCookies } from '@/code/lib/utils/cookies';
import { fullName } from '@/code/lib/utils/initials';
import { trpc } from '@/code/lib/utils/trpc';

const LOGO_WORDMARK_URL =
  'https://aurium-marketing.b-cdn.net/emails/logo-wordmark.png';

// Calendar scope self-heal is an app-deployment concern only — the employee
// portal has no DB User to probe and its users never opt into calendar
// matching. Mirror middleware's isAppDeployment: anything but 'employee' is
// app. NEXT_PUBLIC_ so the value survives into the client bundle.
const IS_APP_DEPLOYMENT =
  process.env.NEXT_PUBLIC_DEPLOYMENT_TYPE !== 'employee';

type DefaultLayoutProps = {
  children: ReactNode;
  useWhiteLogo?: boolean;
  fixHeader?: boolean;
  topPadding?: boolean;
  topBarComponent?: ReactNode;
  useDashboardTopBar?: boolean;
  unScrollable?: boolean;
  forceShowNewCampaignButton?: boolean;
};

export const DefaultLayoutShell = ({
  children,
  ...props
}: DefaultLayoutProps) => {
  return (
    <DialogProvider>
      <PostHogProviderWrapper>
        <WrappedInClerk {...props}>{children}</WrappedInClerk>
      </PostHogProviderWrapper>
    </DialogProvider>
  );
};

export const DefaultLayout = ({ children, ...props }: DefaultLayoutProps) => {
  return (
    <ClerkProvider
      signUpUrl="/sign-up"
      signInUrl="/sign-in"
      appearance={{
        layout: {
          shimmer: false,
          socialButtonsVariant: 'iconButton',
        },
      }}
    >
      <AuthQueryBoundary>
        <DefaultLayoutShell {...props}>{children}</DefaultLayoutShell>
      </AuthQueryBoundary>
    </ClerkProvider>
  );
};

const NavbarSkeleton = () => (
  <div className="flex items-center justify-between p-4 bg-white">
    <div className="flex items-center">
      <Skeleton className="w-24 h-6" />
    </div>
    <div className="flex items-center gap-4">
      <Skeleton className="w-8 h-8 rounded-full" />
    </div>
  </div>
);

/** Shown in place of the whole app when the signed-in user has been offboarded. */
const OffboardedAccessNotice = () => (
  <div className="flex min-h-screen w-full flex-col items-center justify-center gap-6 bg-white p-8">
    <div className="max-w-md space-y-1 text-center text-gray-600">
      <p>You do not have access to this application.</p>
      <p>
        Please contact{' '}
        <a
          href="mailto:support@aurium.ai"
          className="font-medium text-primary hover:underline"
        >
          support@aurium.ai
        </a>{' '}
        if you believe this is a mistake.
      </p>
    </div>
    {/* Give offboarded users a way out — they keep a valid session otherwise. */}
    <SignOutButton>
      <button
        type="button"
        className="text-sm font-medium text-primary hover:underline"
      >
        Sign out
      </button>
    </SignOutButton>
  </div>
);

const WrappedInClerk = ({
  children,
  useWhiteLogo = false,
  fixHeader = false,
  topPadding = false,
  topBarComponent,
  forceShowNewCampaignButton = false,
  unScrollable = false,
  ...props
}: DefaultLayoutProps) => {
  const { user, isLoaded, isSignedIn } = useUser();
  const utils = trpc.useUtils();

  // Lives here (inside ClerkProvider) rather than in DefaultLayout so we can gate
  // the fetch on the live Clerk session — DefaultLayout renders the provider and
  // can't read auth state, which left this query permanently disabled before.
  const currentUserQuery = trpc.user.clerkUserData.useQuery(undefined, {
    enabled: isSignedIn ?? false,
  });
  const currentUser = currentUserQuery.data;
  const dbUser = currentUser?.user ?? null;

  useEffect(() => {
    if (!currentUserQuery.isSuccess) {
      return;
    }
    try {
      sessionStorage.removeItem(NO_SESSION_REDIRECT_LATCH_KEY);
    } catch {
      // sessionStorage can throw
    }
  }, [currentUserQuery.isSuccess]);

  useEffect(() => {
    if (currentUser) {
      // Set user information in Sentry when user data is available
      // user may be null for employees without DB User records
      if (currentUser.user) {
        setUserForSentry({
          id: currentUser.user.id,
          email: currentUser.user.primaryEmail ?? '',
          username: fullName(currentUser.user),
          // Add more useful context for debugging
          orgId: currentUser.user.organizationId ?? undefined,
          isImpersonated: currentUser.impersonated ?? false,
          displayName: currentUser.displayName,
          // Add any other useful user data for your Sentry traces
        });
      }

      const immediatePrefetchQueries: Array<() => Promise<unknown>> = [];
      const laterPrefetchQueries: Array<() => Promise<unknown>> = [];

      if (currentUser.user?.organizationId) {
        laterPrefetchQueries.push(
          ...[
            utils.organizations.getOrganization.prefetch,
            utils.organizations.getTeamMembers.prefetch,
          ],
        );
      }
      if (currentUser.user) {
        laterPrefetchQueries.push(
          ...[
            utils.campaign.getDraft.prefetch,
            utils.campaign.getLeadUpdates.prefetch,
          ],
        );
      }
      // getAllConfigs is deliberately NOT prefetched globally: it is only used
      // by the campaign create/edit flow, which already fetches it on demand.
      // Prefetching it here loaded that payload on every page (incl. /home).
      // Defer to idle so this prefetch clump doesn't compete with the page's
      // critical query in the opening request wave.
      const queries = chunk(
        immediatePrefetchQueries.concat(laterPrefetchQueries),
        5,
      );
      const runPrefetch = () => {
        for (const group of queries) {
          void Promise.all(group.map((query) => query()));
        }
      };
      if (typeof window !== 'undefined' && 'requestIdleCallback' in window) {
        const idleId = window.requestIdleCallback(runPrefetch, {
          timeout: 3000,
        });
        return () => window.cancelIdleCallback(idleId);
      } else {
        const timeoutId = setTimeout(runPrefetch, 1500);
        return () => clearTimeout(timeoutId);
      }
    } else {
      // Clear user data when not available (like on logout)
      clearUserForSentry();
    }
  }, [currentUser, utils]);

  // Simplify: Show dashboard top bar if user has an organization, unless explicitly disabled
  const userBelongsToOrg = !!currentUser?.user?.organizationId;
  const useDashboardTopBar =
    'useDashboardTopBar' in props ? props.useDashboardTopBar : userBelongsToOrg;

  const hasAttachedReferral = useRef(false);
  const attachReferralMutation = trpc.user.attachReferral.useMutation();

  // Handle referral attachment for signed-in users
  // biome-ignore lint/correctness/useExhaustiveDependencies: tRPC mutations cause infinite loops when added to deps
  useEffect(() => {
    if (currentUser?.user?.id && !hasAttachedReferral.current) {
      const referralCodes = getAllCookies('auriumReferralCode');

      if (referralCodes.length > 0) {
        hasAttachedReferral.current = true;

        // Process all referral codes
        Promise.all(
          referralCodes.map((referralCode) =>
            attachReferralMutation.mutateAsync({ referralCode }).catch(() => {
              // Failed to attach this referral, continue with others
              return null;
            }),
          ),
        ).then((results) => {
          const successCount = results.filter((r) => r?.success).length;
          if (successCount === 0 && referralCodes.length > 0) {
            // All failed - allow retry
            hasAttachedReferral.current = false;
          }
        });
      }
    }
  }, [currentUser?.user?.id]);

  const [showShadow, setShowShadow] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      const newScroll = {
        x: window.pageXOffset,
        y: window.pageYOffset,
      };

      if (newScroll.y > 10) {
        setShowShadow(true);
      } else {
        setShowShadow(false);
      }
    };

    window.addEventListener('scroll', handleScroll);

    // Call handleScroll initially to set the initial scroll position
    handleScroll();

    // Clean up the event listener on component unmount
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const colorOfDropdown =
    (user?.organizationMemberships.length ?? 0 > 0)
      ? useWhiteLogo
        ? 'white'
        : 'black'
      : 'transparent';
  const userHasOrganization = (user?.organizationMemberships.length ?? 0) >= 1;

  const userOrganizationSlug =
    user?.organizationMemberships?.[0]?.organization.slug;

  // if the user has an organization, we need to render the top navbar differently

  const { setActive } = useOrganizationList();
  const { organization: activeOrganization, isLoaded: isActiveOrgLoaded } =
    useOrganization();

  const firstOrgId = user?.organizationMemberships?.[0]?.organization.id;
  // Stable dependency for the effect: the set of org ids the user belongs to,
  // collapsed to a string so a new array reference doesn't re-fire the effect.
  const membershipOrgIdsKey =
    user?.organizationMemberships
      ?.map((membership) => membership.organization.id)
      .join(',') ?? '';

  useEffect(() => {
    // Wait until the active-org state is known before reconciling, otherwise we
    // can't tell "no active org" from "not loaded yet".
    if (!setActive || !isActiveOrgLoaded || !firstOrgId) return;

    // Only auto-select when the session has *no* active org, or its active org
    // is no longer one of the user's memberships (stale). Reconciling against
    // live state — not a write-once cache — lets a session that lost its active
    // org (token refresh, role/membership re-sync) recover, instead of being
    // wedged at /entry forever because middleware sees no orgSlug.
    //
    // Do NOT override a valid active org: that would snap multi-org users back
    // to their first org on every switch and fight pages that intentionally set
    // a specific org active (e.g. welcome/organizations setup).
    const membershipOrgIds = membershipOrgIdsKey.split(',');
    if (
      activeOrganization &&
      membershipOrgIds.includes(activeOrganization.id)
    ) {
      return;
    }

    void setActive({ organization: firstOrgId });
  }, [
    setActive,
    isActiveOrgLoaded,
    activeOrganization,
    firstOrgId,
    membershipOrgIdsKey,
  ]);

  // Expose measured header+banner height as --global-header-height so
  // fixed-height descendants (EmployeeLayout) subtract the right offset, tracking
  // the banner showing/hiding. Callback ref reattaches the observer when the node
  // mounts (only in the main return below).
  const [chromeHeight, setChromeHeight] = useState(0);
  const chromeObserverRef = useRef<ResizeObserver | null>(null);
  const chromeRef = useCallback((node: HTMLDivElement | null) => {
    chromeObserverRef.current?.disconnect();
    if (!node) return;
    const observer = new ResizeObserver(() => {
      setChromeHeight(node.offsetHeight);
    });
    observer.observe(node);
    chromeObserverRef.current = observer;
    setChromeHeight(node.offsetHeight);
  }, []);

  if (!isLoaded) {
    return (
      <>
        <NavbarSkeleton />
        <div
          className={cn(
            topPadding && 'pt-16',
            unScrollable && 'flex-1 flex flex-col min-h-0 overflow-hidden',
          )}
        >
          {children}
        </div>
      </>
    );
  }

  // Offboarded users keep a valid Clerk session but lose all app access — show a
  // minimal notice instead of the dashboard. Employees have a null `user`, so
  // they're unaffected. Impersonation deliberately DOES show the notice (so an
  // employee can preview exactly what the offboarded user sees); the tRPC gate
  // stays exempt during impersonation so the employee's requests aren't 403'd.
  if (currentUser?.user?.offboardedAt) {
    return <OffboardedAccessNotice />;
  }

  return (
    <>
      <FullstoryProvider />
      <GoogleAnalytics gaId="G-JCXXJ5W345" />
      {dbUser && <Notifications />}
      <CmdKSwitcher dbUser={dbUser} isSignedIn={Boolean(isSignedIn)} />
      <div
        className={cn(
          'w-full min-h-screen bg-white',
          unScrollable && 'h-screen flex flex-col overflow-hidden',
        )}
        style={
          { '--global-header-height': `${chromeHeight}px` } as CSSProperties
        }
      >
        <div ref={chromeRef}>
          <header
            className={cn(
              'flex z-10 justify-between items-center p-4 w-full',
              showShadow && 'shadow-md',
              fixHeader && 'fixed',
              !useWhiteLogo && 'bg-white',
            )}
          >
            <div className="flex gap-6 items-center">
              <Link
                href={
                  userHasOrganization && userOrganizationSlug
                    ? LinkRouter.routeForOrganizationHome(userOrganizationSlug)
                    : LinkRouter.routeForDashboard()
                }
                className="flex shrink-0 items-center"
              >
                <Image
                  src={LOGO_WORDMARK_URL}
                  alt="Aurium"
                  width={120}
                  height={30}
                  // PNG is bottom-weighted (transparent padding on top); nudge up to optically center against the org switcher
                  className="h-8 w-auto -translate-y-0.5 cursor-pointer"
                />
              </Link>
              {userHasOrganization && (
                <OrganizationSwitcher
                  hidePersonal={true}
                  organizationProfileProps={{
                    appearance: {
                      elements: {
                        navbarButton__members: { display: 'none' },
                        profilePage__organizationMembers: { display: 'none' },
                        profileSection__organizationDanger: { display: 'none' },
                      },
                    },
                  }}
                  appearance={{
                    elements: {
                      organizationSwitcherPopoverActionButton__createOrganization:
                        { display: 'none' },
                      userPreviewTextContainer__personalWorkspace: {
                        appearance: 'none',
                        color: 'transparent',
                      },
                      organizationSwitcherTrigger: {
                        color: colorOfDropdown,
                        '&:hover': {
                          color: colorOfDropdown,
                        },
                        '&:focus': {
                          color: colorOfDropdown,
                        },
                      },
                    },
                  }}
                >
                  <OrganizationSwitcher.OrganizationProfilePage
                    label="Team"
                    labelIcon={<UsersIcon className="h-4 w-4" />}
                    url="team"
                  >
                    <TeamManagementPage />
                  </OrganizationSwitcher.OrganizationProfilePage>
                </OrganizationSwitcher>
              )}

              {isSignedIn && !userHasOrganization && (
                <DashboardNavigation
                  dbUser={dbUser}
                  forceShowNewCampaignButton={forceShowNewCampaignButton}
                  useDashboardTopBar={!!useDashboardTopBar}
                />
              )}
              {useDashboardTopBar &&
                userHasOrganization &&
                dbUser?.organizationId && (
                  <OrganizationNavigation
                    organizationSlug={userOrganizationSlug!}
                  />
                )}
              {topBarComponent}
            </div>

            <SignedOut>{/* display nothing */}</SignedOut>
            <SignedIn>
              {/* Effect-only: silently re-grants a clobbered calendar scope for
                  opted-in app users. Employee deployment is gated out. */}
              {IS_APP_DEPLOYMENT && dbUser && <CalendarScopeAutoHeal />}
              <div className="flex flex-row gap-4">
                <CustomizedUserButton dbUser={dbUser} />
              </div>
            </SignedIn>
          </header>

          {/* relative z-20 so the banner sits above the (sometimes fixed,
              sometimes transparent) header (z-10) and stays clickable. */}
          <SignedIn>
            <div className="relative z-20">
              <PendingInvitationsBanner />
            </div>
          </SignedIn>
        </div>

        <div
          className={cn(
            topPadding && 'pt-16',
            unScrollable && 'flex-1 flex flex-col min-h-0 overflow-hidden',
          )}
        >
          {children}
        </div>
      </div>
      <Toaster />
    </>
  );
};
